import React, { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import PackageIndexItem from "./PackageIndexItem";
import { PackageIndexDrawerWrapper } from "./PackageIndexUI";
import { PACKAGE_ACTIONS, usePackage, usePackageDispatch } from "contexts/PackageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { duotone, icon, light } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ConfigContext } from "contexts/ConfigContext";
import { BookLayoutContext } from "contexts/BookLayoutContext";
import { Tooltip } from "antd";
import { groupModelsByGroupName } from "utils/modelsGroups";
import { checkGroupsForCompCards } from "utils/modelPlaylists";
import ShortlistSendDrawer from "components/Shortlist/ShortlistSendDrawer";

const PackageIndex = ({ currentModel, activePlaylist }) => {
    const history = useHistory();

    const handleMoveToModel = (model) => {
        history.push({
            pathname: `/ebook/${packageCode}/model/${model.ID}`,
        });
    };
    const { currentPack, packageCode, useShortlist, canSendShortlist, messageDrawerOpen } = usePackage();
    const dispatch = usePackageDispatch();
    const { Favourites: favs, Models: models } = currentPack;
    const { clientConfig } = useContext(ConfigContext);
    const { headerHeight } = useContext(BookLayoutContext);
    const { compcardTemplates, exportOptions } = clientConfig;
    const canDownloadCompCards = Boolean(exportOptions.pdfCompcard && compcardTemplates && compcardTemplates.length);

    // stesso meccanismo di PlaylistsNav: l'ancora #playlist-N sta nel container #modelPlaylistsWrapper (vedi Model.js)
    const handleMoveToPlaylist = (index) => {
        const scroller = document.getElementById("modelPlaylistsWrapper");
        const anchor = document.getElementById(`playlist-${index}`);
        if (!scroller || !anchor) {
            return;
        }
        const top =
            scroller.scrollTop +
            (anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top) -
            headerHeight;
        scroller.scrollTo({ top, behavior: "smooth" });
    };

    const parsedModels = useMemo(() => {
        let shortlist = { Models: [], hasCompCard: false, hasAllCompCards: false, numberOfCompCardsAvailable: 0 };
        if (useShortlist) {
            shortlist.Models = models.filter((model) => favs.some((fav) => fav.ModelId === model.ModelID));
            shortlist = checkGroupsForCompCards(shortlist);
        }
        const modelsNotInShortlist = useShortlist
            ? models.filter((model) => !favs.some((fav) => fav.ModelId === model.ModelID))
            : models;
        const { grouped, notGrouped } = groupModelsByGroupName(modelsNotInShortlist);
        const groupedModels = grouped.map((group) => {
            return checkGroupsForCompCards(group);
        });
        const noGroupModels = checkGroupsForCompCards(notGrouped);
        return {
            shortlist,
            groups: [...groupedModels, noGroupModels],
        };
    }, [models, favs, useShortlist]);

    let itemHeight = "8rem";
    if (models.length > 10) {
        itemHeight = "6rem";
    }
    if (models.length > 20) {
        itemHeight = "4rem";
    }

    const downloadCompCards = (modelIDs) => {
        //get the first compcard playlist ID for each model in modelIDs
        const selectedModelsWithPlaylists = models.filter(
            (model) => modelIDs.includes(model.ModelID) && model.Playlists.some((playlist) => playlist.CompCard)
        );
        //get ID of the first compcard playlist for each model
        const packagePlaylistIDs = selectedModelsWithPlaylists.map(
            (model) => model.Playlists.find((playlist) => playlist.CompCard).ID
        );
        const packagePlaylistIDsString = packagePlaylistIDs.join("-");

        const template = compcardTemplates[0];

        let url = "/api/packages/printplaylists?";
        url += "PackagePlaylistIDs=" + packagePlaylistIDsString;
        url += "&PackageCode=" + packageCode;
        url += "&Template=" + template.file;
        url += "&IsCustom=" + (template.isCustom ? "true" : "false");
        url += "&Orientation=" + template.orientation;
        url += "&FileName=compcards";
        window.open(url, "_blank");
    };

    const openShortlistMessageDrawer = () => {
        dispatch({ type: PACKAGE_ACTIONS.SET_MESSAGE_DRAWER_OPEN, payload: true });
    };

    return (
        <PackageIndexDrawerWrapper $itemHeight={itemHeight}>
            {parsedModels.shortlist.Models.length > 0 && (
                <div className="models-group-container">
                    <h2 className="models-group-name">
                        <FontAwesomeIcon
                            size="xs"
                            style={{ marginRight: ".5rem" }}
                            icon={icon({ name: "heart", family: "sharp", style: "solid" })}
                        />
                        Your Shortlist
                    </h2>
                    {(parsedModels.shortlist.hasCompCard || canSendShortlist) && (
                        <div className="models-group-toolbar-icons">
                            {canSendShortlist && (
                                <div className="send-shortlist" onClick={openShortlistMessageDrawer}>
                                    <Tooltip placement="left" title="send shortlist to booker">
                                        <span className="fa-layers fa-fw fa-lg">
                                            <FontAwesomeIcon icon={light("paper-plane")} />
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                            {parsedModels.shortlist.hasCompCard && canDownloadCompCards && (
                                <div
                                    className="download-compcards"
                                    onClick={() =>
                                        downloadCompCards(parsedModels.shortlist.Models.map((m) => m.ModelID))
                                    }
                                >
                                    <Tooltip
                                        placement="left"
                                        title={`${
                                            parsedModels.shortlist.hasAllCompCards
                                                ? "download compcards"
                                                : " download available compcards (" +
                                                  parsedModels.shortlist.numberOfCompCardsAvailable +
                                                  " of " +
                                                  parsedModels.shortlist.Models.length +
                                                  ")"
                                        }`}
                                    >
                                        <span className="fa-layers fa-fw fa-lg">
                                            <FontAwesomeIcon icon={light("image-polaroid-user")} />
                                            <FontAwesomeIcon
                                                className="arrow-down"
                                                icon={duotone("circle-arrow-down")}
                                                transform="shrink-8 down-5 right-6"
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="models-container">
                        {parsedModels.shortlist.Models.map((model) => {
                            return (
                                <PackageIndexItem
                                    inShortlist
                                    key={`${model.ID}`}
                                    isCurrent={currentModel ? model.ID === currentModel.ID : false}
                                    model={model}
                                    onModelClick={handleMoveToModel}
                                    activePlaylist={activePlaylist}
                                    onPlaylistClick={handleMoveToPlaylist}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
            {parsedModels.groups.map((modelGroup, index) => {
                return modelGroup.Models.length === 0 ? (
                    <div key={`model-group-${index}`} />
                ) : (
                    <div className="models-group-container" key={`model-group-${index}`}>
                        {modelGroup.GroupName !== "no group" || index !== 0 ? (
                            <h2 className="models-group-name">{modelGroup.GroupName}</h2>
                        ) : (
                            <h2 className="models-group-name">Index</h2>
                        )}
                        {modelGroup.hasCompCard && canDownloadCompCards && (
                            <div className="models-group-toolbar-icons">
                                <div
                                    className="download-compcards"
                                    onClick={() => downloadCompCards(modelGroup.Models.map((m) => m.ModelID))}
                                >
                                    <Tooltip
                                        placement="left"
                                        title={`${
                                            modelGroup.hasAllCompCards
                                                ? "download compcards"
                                                : " download available compcards (" +
                                                  modelGroup.numberOfCompCardsAvailable +
                                                  " of " +
                                                  modelGroup.Models.length +
                                                  ")"
                                        }`}
                                    >
                                        <span className="fa-layers fa-fw fa-lg">
                                            <FontAwesomeIcon icon={light("image-polaroid-user")} />
                                            <FontAwesomeIcon
                                                className="arrow-down"
                                                icon={duotone("circle-arrow-down")}
                                                transform="shrink-8 down-5 right-6"
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                        )}
                        <div className={`models-container`}>
                            {modelGroup.Models.map((model) => {
                                return (
                                    <PackageIndexItem
                                        key={`${model.ID}`}
                                        isCurrent={currentModel ? model.ID === currentModel.ID : false}
                                        model={model}
                                        onModelClick={handleMoveToModel}
                                        activePlaylist={activePlaylist}
                                        onPlaylistClick={handleMoveToPlaylist}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            {messageDrawerOpen && <ShortlistSendDrawer shortlistModels={parsedModels.shortlist.Models} />}
        </PackageIndexDrawerWrapper>
    );
};

export default PackageIndex;
